import * as React from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import {useTheme} from "@mui/material/styles";
import {formatDateForAPI} from "../utils/datetime";
import {TradingBacktestResult, TradingAlgoStored} from "../utils/typesTrading";
import Typography from "@mui/material/Typography";
import {formatCurrency} from "../utils/financial";
import {TradeHistoryTable} from "./BacktestTradeHistoryTable";
import {SummaryStatsTable} from "./BacktestSummaryResultsTable";
import {ChartContainer} from "@mui/x-charts/ChartContainer";
import {LinePlot, LineHighlightPlot} from "@mui/x-charts/LineChart";
import {ScatterPlot} from "@mui/x-charts/ScatterChart";
import {ChartsXAxis} from "@mui/x-charts/ChartsXAxis";
import {ChartsYAxis} from "@mui/x-charts/ChartsYAxis";
import {ChartsTooltip} from "@mui/x-charts/ChartsTooltip";
import {ChartsLegend} from "@mui/x-charts/ChartsLegend";
import {ChartsGrid} from "@mui/x-charts/ChartsGrid";
import {ChartsAxisHighlight, MarkPlot} from "@mui/x-charts";

interface BacktestResultsGraphProps {
    selectedTradingAlgo: TradingAlgoStored | null;
    backtestStartDate: Date;
    backtestEndDate: Date;
    backtestDates: number[];
    stratPctReturns: number[];
    bmarkPctReturns: number[];
    stratCashValues: number[];
    backtestResult: TradingBacktestResult | null;
    onGoBack: () => void;
}

export const BacktestResultsGraph: React.FC<BacktestResultsGraphProps> = ({
    selectedTradingAlgo,
    backtestStartDate,
    backtestEndDate,
    backtestDates,
    stratPctReturns,
    bmarkPctReturns,
    stratCashValues,
    backtestResult,
    onGoBack
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));

    const chartWidth = isMobile ? 720 : isTablet ? 720 : 1500;
    const chartHeight = isMobile ? 500 : isTablet ? 500 : 700;

    const tradeScatterData = React.useMemo(() => {
        if (!backtestResult?.trade_history?.length || !backtestDates.length) {
            return { buys: [], sells: [] };
        }
        const buys: { x: number; y: number; id: string }[] = [];
        const sells: { x: number; y: number; id: string }[] = [];

        backtestResult.trade_history.forEach((trade, i) => {
            const tradeTs = new Date(trade.trade_date).getTime();
            // Find the nearest date index in backtestDates
            let nearestIdx = 0;
            let minDiff = Math.abs(backtestDates[0] - tradeTs);
            for (let j = 1; j < backtestDates.length; j++) {
                const diff = Math.abs(backtestDates[j] - tradeTs);
                if (diff < minDiff) { minDiff = diff; nearestIdx = j; }
            }
            const point = { x: backtestDates[nearestIdx], y: stratPctReturns[nearestIdx], id: `trade-${i}` };
            if (trade.action?.toUpperCase().includes('BUY')) buys.push(point);
            else sells.push(point);
        });

        return { buys, sells };
    }, [backtestResult, backtestDates, stratPctReturns]);

    return (
        <Box sx={{ mt: { xs: 3, sm: 6 }, width: "100%" }}>
            {(selectedTradingAlgo
                && backtestDates.length > 0
                && backtestDates.length === stratPctReturns.length
                && stratPctReturns.length === bmarkPctReturns.length) ? (
                <Box
                    sx={{
                        mt: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        width: "100%",
                        px: { xs: 1, sm: 2 },
                    }}
                >
                    <Typography
                        variant={isMobile ? "body2" : "body1"}
                        sx={{
                            textAlign: "center",
                            mb: { xs: 1, sm: 2 },
                            maxWidth: "100%",
                            overflowWrap: "break-word",
                        }}
                    >
                        {`${selectedTradingAlgo.strategy_name || 'Strategy'} `} Results {`(${formatDateForAPI(backtestStartDate)} to ${formatDateForAPI(backtestEndDate)})`}
                    </Typography>

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: chartWidth,
                            overflowX: "auto",
                            overflowY: "hidden",
                            WebkitOverflowScrolling: "touch",
                        }}
                    >
                    <ChartContainer
                        // force the chart to remount when underlying data changes
                        key={[
                            formatDateForAPI(backtestStartDate),
                            formatDateForAPI(backtestEndDate),
                        ].join("-")}
                        width={chartWidth}
                        height={chartHeight}
                        xAxis={[{
                            data: backtestDates,
                            scaleType: 'time',
                            min: backtestStartDate,  // must be a Date object
                            max: backtestEndDate,
                            domainLimit: 'strict',  // prevents MUI's "nice" rounding from re-expanding the range
                            valueFormatter: (value: number) => {
                                const date = new Date(value);
                                return isMobile
                                    ? date.toLocaleDateString('en-US', { month: 'numeric', day: '2-digit' })
                                    : date.toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' });
                            },
                            label: isMobile ? undefined : 'Date',
                            id: 'x-axis',
                        }]}
                        yAxis={[{
                            label: isMobile ? undefined : "Percent Returns",
                            id: 'y-axis'
                        }]}
                        series={[
                            ...(stratPctReturns.length > 0 ? [{
                                type: 'line' as const,
                                data: stratPctReturns,
                                label: selectedTradingAlgo.strategy_name || 'Strategy',
                                showMark: false,
                                curve: 'linear' as const,
                                valueFormatter: (value: any, { dataIndex }: {dataIndex: number }) => {
                                    const cash = stratCashValues?.[dataIndex];
                                    const pctStr = value != null ? `${value.toFixed(2)}%` : 'N/A';
                                    const cashStr = cash != null ? formatCurrency(cash) : 'N/A';
                                    return `${pctStr} (${cashStr})`;
                                },
                            }] : []),
                            ...(bmarkPctReturns.length > 0 ? [{
                                type: 'line' as const,
                                data: bmarkPctReturns,
                                label: 'SPY',
                                showMark: false,
                                curve: 'linear' as const,
                                valueFormatter: (value: any) => value != null ? `${value.toFixed(2)}%` : 'N/A',
                            }] : []),
                            ...(tradeScatterData.buys.length > 0 ? [{
                                type: 'scatter' as const,
                                data: tradeScatterData.buys,
                                label: '',
                                xAxisId: 'x-axis',
                                yAxisId: 'y-axis',
                                color: '#2e7d32',
                                valueFormatter: (value: any) => value != null ? `${new Date(value.x).toLocaleDateString()}` : null,
                            }] : []),
                            ...(tradeScatterData.sells.length > 0 ? [{
                                type: 'scatter' as const,
                                data: tradeScatterData.sells,
                                label: '',
                                xAxisId: 'x-axis',
                                yAxisId: 'y-axis',
                                color: '#c62828',
                                valueFormatter: (value: any) => value != null ? `${new Date(value.x).toLocaleDateString()}` : null,
                            }] : []),
                        ]}
                        sx={{
                            '.MuiChartsAxis-bottom .MuiChartsAxis-tickLabel': {
                                transform: isMobile ? 'rotate(-45deg)' : 'rotate(-30deg)',
                                textAnchor: 'end',
                                fontSize: isMobile ? 10 : 12,
                            },
                            '.MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                                fontSize: isMobile ? 10 : 12,
                            },
                            '.MuiChartsAxis-bottom .MuiChartsAxis-label': {
                                transform: 'translateY(20px)',
                            }
                        }}
                    >
                        {/*<ChartsGrid horizontal={true}/>*/} {/* Enable to show horizontal / vertical grid lines */}
                        <LinePlot /> {/*Show the lines */}
                        <ChartsAxisHighlight x={"line"} y={"none"} /> {/* Vertical line*/}
                        <LineHighlightPlot/> {/* Show markers on the lines during hover */}
                        <ScatterPlot /> {/* Shows the scatter plot */}
                        <ChartsXAxis axisId="x-axis" />
                        <ChartsYAxis axisId="y-axis" />
                        <ChartsTooltip />
                    </ChartContainer>
                    </Box>

                    {backtestResult && <SummaryStatsTable backtestResult={backtestResult} />}
                    {backtestResult && backtestResult.trade_history.length > 0 && (
                        <TradeHistoryTable trades={backtestResult.trade_history} code={selectedTradingAlgo?.code} />
                    )}
                </Box>
            ) : (
                <div></div>
            )}
        </Box>
    );
};
